import { useState } from "react";
import { useController } from "react-hook-form";
import { Stack, Text, UnstyledButton } from "@mantine/core";
import { IconCircleX } from "@tabler/icons-react";
import { Button } from "@onengine/component-library";
import type { FormQuestion } from "@/components/features/FormBuilder/types/form.types";
import type { FormLogicContext } from "@/components/features/FormBuilder/data/contextTriggers";
import { stripHiddenEntryValues } from "@/components/features/FormBuilder/utils/evaluateLogic";
import { getRowDisplay } from "@/components/features/FormBuilder/utils/renderRowTemplate";
import { generateId } from "@/store/form-builder";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import { NoDataCreatedCard } from "@/components/ui/NoDataCreatedCard/NoDataCreatedCard";
import { FormErrorBox } from "@/components/ui/FormErrorBox/FormErrorBox";
import { GroupEntryEditor } from "./GroupEntryEditor";
import type { ControlledFieldProps } from "./types";
import styles from "./ControlledRepeatingGroup.module.scss";

type GroupEntry = Record<string, unknown> & { _itemId: string };

export function ControlledRepeatingGroup({
  name,
  control,
  error,
  clearErrors,
  group,
  isMobile,
  logicContext,
}: ControlledFieldProps & {
  group: FormQuestion;
  isMobile?: boolean;
  logicContext?: FormLogicContext;
}) {
  const { field } = useController({ name, control, defaultValue: [] });
  const items: GroupEntry[] = Array.isArray(field.value) ? field.value : [];

  const [editingItem, setEditingItem] = useState<GroupEntry | null>();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);

  const itemLabel = group.groupConfig?.itemLabel || "Item";
  const addLabel = group.groupConfig?.addLabel || `Add ${itemLabel}`;
  const maxReached =
    group.groupConfig?.maxItems !== undefined && items.length >= group.groupConfig.maxItems;

  const openAdd = () => {
    setEditingItem(null);
    setIsEditorOpen(true);
  };

  const openEdit = (item: GroupEntry) => {
    setEditingItem(item);
    setIsEditorOpen(true);
  };

  const closeEditor = () => setIsEditorOpen(false);

  const handleSave = (values: Record<string, unknown>) => {
    const stripped = stripHiddenEntryValues(group, values, logicContext);
    if (editingItem) {
      field.onChange(
        items.map((it) =>
          it._itemId === editingItem._itemId ? { ...stripped, _itemId: it._itemId } : it,
        ),
      );
    } else {
      field.onChange([...items, { ...stripped, _itemId: generateId("item") }]);
    }
    clearErrors(name);
    closeEditor();
  };

  const handleRemoveConfirm = () => {
    if (!pendingRemoveId) return;
    field.onChange(items.filter((it) => it._itemId !== pendingRemoveId));
    setPendingRemoveId(null);
  };

  return (
    <Stack gap="sm">
      {items.length === 0 ? (
        <NoDataCreatedCard dataLabel={itemLabel} ctaText={addLabel} />
      ) : (
        <Stack gap="xs">
          {items.map((item) => {
            const { title, subtitle } = getRowDisplay(group, item, logicContext);
            return (
              <div
                key={item._itemId}
                role="button"
                tabIndex={0}
                className={styles.row}
                onClick={() => openEdit(item)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openEdit(item);
                  }
                }}
              >
                <Stack gap={2} className={styles.rowText}>
                  <Text size="sm" fw={600} c="var(--text-strong)" truncate>
                    {title || "Untitled"}
                  </Text>
                  {subtitle && (
                    <Text size="xs" c="var(--text-weak)" truncate>
                      {subtitle}
                    </Text>
                  )}
                </Stack>
                <UnstyledButton
                  aria-label={`Remove ${itemLabel}`}
                  className={styles.removeButton}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPendingRemoveId(item._itemId);
                  }}
                >
                  <IconCircleX size={20} />
                </UnstyledButton>
              </div>
            );
          })}
        </Stack>
      )}

      {error && <FormErrorBox message={error} />}

      <Button variant="primary" size="base" onClick={openAdd} isDisabled={maxReached}>
        + {addLabel}
      </Button>

      {isEditorOpen && (
        <GroupEntryEditor
          group={group}
          item={editingItem ?? null}
          isMobile={isMobile}
          logicContext={logicContext}
          onCancel={closeEditor}
          onSave={handleSave}
        />
      )}

      <ConfirmationModal
        isOpen={!!pendingRemoveId}
        onClose={() => setPendingRemoveId(null)}
        title={`Remove ${itemLabel}`}
        body={`Are you sure you want to remove this ${itemLabel.toLowerCase()}? This cannot be undone.`}
        confirmText="Remove"
        confirmVariant="destructive-primary"
        onConfirm={handleRemoveConfirm}
      />
    </Stack>
  );
}
